import { isAdminHost } from "@/lib/adminHost";
import { mediaVariant, resolveImage } from "@/lib/media";

const MANIFEST_ID = "app-manifest";

// The platform mark is the same everywhere a city's own colours aren't — "the platform is the
// mark, the city is named underneath" (CLAUDE.md). This used to draw a per-city circle+initial
// favicon (the pre-rebrand CityLogo look), left in place by the Sept 2026 brand-lockup rework
// which only touched title/manifest text. Static files, not a canvas redraw, now that there's one
// mark for every city.
export function setFavicon() {
  document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => el.remove());
  const icon = document.createElement("link");
  icon.rel = "icon";
  icon.type = "image/png";
  icon.href = "/icons/favicon-48.png";
  document.head.appendChild(icon);
  const apple = document.createElement("link");
  apple.rel = "apple-touch-icon";
  apple.href = "/icons/apple-touch-icon.png";
  document.head.appendChild(apple);
}

function hslToHex(value) {
  const match = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(value || "");
  if (!match) return "#0f7a56";
  const h = Number(match[1]);
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(h / 60) % 6;
  const rgb = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][seg].map((v) => Math.round((v + m) * 255));
  return `#${rgb.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/** `og:image` / `twitter:image` = the city's own hero at the `detail` (1600w) variant, as an ABSOLUTE
 * URL (a scraper has no base). No hero -> the tags are removed rather than left pointing at the
 * previous city. Set from JS, so only crawlers that run scripts see it. */
function setShareImage(heroUrl) {
  const absolute = heroUrl ? new URL(resolveImage(mediaVariant(heroUrl, "detail")), window.location.origin).href : "";
  [
    ["property", "og:image"],
    ["name", "twitter:image"],
  ].forEach(([attr, key]) => {
    let tag = document.querySelector(`meta[${attr}="${key}"]`);
    if (!absolute) {
      if (tag) tag.remove();
      return;
    }
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute(attr, key);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", absolute);
  });
}

export function applyCityBranding(tenant, theme) {
  const cityName = tenant?.name || "Biswan";
  const appName = `Apna Shahar · ${cityName} Online`;
  const themeColor = hslToHex(theme?.colors?.primary);
  document.title = appName;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", themeColor);
  setFavicon();
  setShareImage(tenant?.hero_image_url);

  const manifest = {
    name: appName,
    short_name: "Apna Shahar",
    description: tenant?.tagline || "Your city in one app.",
    start_url: `/${tenant?.slug || ""}`,
    scope: `/${tenant?.slug || ""}`,
    display: "standalone",
    orientation: "portrait",
    background_color: "#ffffff",
    theme_color: themeColor,
    icons: [
      { src: "/icons/icon-192.png", sizes: "192x192", type: "image/png", purpose: "any" },
      { src: "/icons/icon-512.png", sizes: "512x512", type: "image/png", purpose: "any" },
      { src: "/icons/icon-512.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
    ],
  };

  let link = document.getElementById(MANIFEST_ID);
  if (!link) {
    link = document.createElement("link");
    link.id = MANIFEST_ID;
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  if (link.dataset.blobUrl) URL.revokeObjectURL(link.dataset.blobUrl);
  const blobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }));
  link.dataset.blobUrl = blobUrl;
  link.href = blobUrl;
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  // The admin host serves a different, security-sensitive surface and must NOT be controlled by the
  // citizen PWA worker. The owner's browser had the citizen `sw.js` (registered while visiting a
  // city app) still controlling the admin host, serving stale cached responses. So on the admin
  // host we register nothing AND actively tear down any worker + CacheStorage the citizen app left
  // behind, rather than merely skipping registration.
  if (isAdminHost()) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()))
        .catch(() => {});
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
      }
    });
    return;
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
